// Client layer (TS-2.5.03). Remediates the two focus-related WCAG AA gaps decomposition.md
// section 8.7 point 11 names in the recovered reference output: chips there are non-focusable
// `<span>`s whose only detail is a `title` tooltip (unreachable by keyboard). Here a chip is a
// real <button>, and hover, focus AND click all reveal the same detail popover (BRD section 8.2's
// "hover shows name/status/%", made keyboard-reachable) - Escape closes it and returns focus to
// the chip, per the roadmap board's own keyboard contract.
import { useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { readableTextColor } from "./lib/contrast";
import type { BoardChip, ProjectView } from "../../api/generated/contracts";
import { strings } from "../../strings";

export interface ProjectChipProps {
  readonly chip: BoardChip;
  readonly dimmed: boolean;
  readonly dimmedOpacity: number;
  readonly columnLabel: string;
  readonly teamGroupLabel: string;
  readonly detail: ProjectView | undefined;
}

function formatPercent(percentComplete: number | null): string {
  return percentComplete === null ? "—" : `${Math.round(percentComplete)}%`;
}

function summarizeNotes(detail: ProjectView): string {
  const riskCount = detail.risks.length;
  const decisionCount = detail.decisions.length;
  if (riskCount === 0 && decisionCount === 0) return "No open risks or decisions.";
  return `${riskCount} open risk${riskCount === 1 ? "" : "s"}, ${decisionCount} open decision${decisionCount === 1 ? "" : "s"}.`;
}

export function ProjectChip({ chip, dimmed, dimmedOpacity, columnLabel, teamGroupLabel, detail }: ProjectChipProps) {
  const [revealed, setRevealed] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  function reveal() {
    setRevealed(true);
  }

  function hide() {
    setRevealed(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      reveal();
    } else if (event.key === "Escape" && revealed) {
      event.preventDefault();
      hide();
      buttonRef.current?.focus();
    }
  }

  const dimmedSuffix = dimmed ? ", does not match the current status filter" : "";
  const ariaLabel = `${chip.label}, ${chip.statusCanonical}, ${columnLabel}, ${teamGroupLabel}${dimmedSuffix}`;

  // Text colour is computed PER CHIP from the status colour's relative luminance (D-247): the
  // vocabulary hex is admin data, so white-for-everyone fails AA on the light statuses (On Track
  // #22C55E is 2.28:1 against white). roadmap.css reads both custom properties.
  const style: CSSProperties = {
    "--eg-chip-color": chip.colorHex,
    "--eg-chip-ink": readableTextColor(chip.colorHex),
    opacity: dimmed ? dimmedOpacity : 1,
  } as CSSProperties;

  return (
    <span className="eg-chip-wrap">
      <button
        ref={buttonRef}
        type="button"
        className="eg-chip"
        style={style}
        data-dimmed={dimmed ? "true" : undefined}
        aria-haspopup="true"
        aria-expanded={revealed}
        aria-label={ariaLabel}
        onMouseEnter={reveal}
        onMouseLeave={hide}
        onFocus={reveal}
        onBlur={hide}
        onClick={reveal}
        onKeyDown={handleKeyDown}
      >
        {chip.label}
      </button>
      {revealed && (
        <div role="group" aria-label={`${chip.label} detail`} className="eg-chip-detail">
          {detail ? (
            <dl>
              <dt>Status</dt>
              <dd>{chip.statusCanonical}</dd>
              <dt>% complete</dt>
              <dd>{formatPercent(detail.percentComplete)}</dd>
              <dt>Go-live</dt>
              <dd>{detail.goLive.label}</dd>
              <dt>Next milestone</dt>
              {/* D-255: this used to read "—" for a milestone-less project (detail.next.label
                  falsy), indistinguishable from a project that simply has no NEXT milestone due.
                  The card's own detection point (milestones.length === 0) is the honest signal. */}
              <dd>
                {detail.milestones.length === 0
                  ? strings.roadmap.noMilestones
                  : detail.next.label
                    ? `${detail.next.label} — ${detail.next.dateLabel}`
                    : "—"}
              </dd>
              <dt>Notes</dt>
              <dd>{summarizeNotes(detail)}</dd>
            </dl>
          ) : (
            <p>Detail unavailable.</p>
          )}
        </div>
      )}
    </span>
  );
}
